import { useCallback } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import type { TranscriptCourseInput } from '@teachur/http-client';

export interface ReviewCourse extends TranscriptCourseInput {
  key: string;
}

export interface ReviewState {
  institution: string;
  institutionUrl: string;
  accreditingOrganizations: string[];
  awardedDegree: string | null;
  officialTranscriptRequested: boolean;
  unofficialCredits: number | null;
  courses: ReviewCourse[];
  unofficialFileId: number | null;
}

export interface ReviewController {
  review: ReviewState;
  aiError: string | null;
  setInstitution: (institution: string) => void;
  setInstitutionUrl: (value: string) => void;
  setAccreditingOrganizations: (value: string[]) => void;
  setAwardedDegree: (value: string | null) => void;
  setUnofficialCredits: (value: number | null) => void;
  addCourse: () => void;
  updateCourse: (key: string, patch: Partial<TranscriptCourseInput>) => void;
  removeCourse: (key: string) => void;
}

export const emptyReview = (): ReviewState => ({
  institution: '',
  institutionUrl: '',
  accreditingOrganizations: [],
  awardedDegree: null,
  officialTranscriptRequested: false,
  unofficialCredits: null,
  courses: [],
  unofficialFileId: null,
});

export const makeKeyGen = (prefix: string) => {
  let seq = 0;
  return () => `${prefix}${++seq}`;
};

export const normalizeGrade = (grade: string | null | undefined): string | null => {
  if (grade == null) return null;
  const g = grade.trim();
  if (g.length === 0) return null;

  const upper = g.toUpperCase();

  if (upper === 'P' || upper === 'PASS' || upper === 'S' || upper === 'SATISFACTORY') {
    return 'CR';
  }

  return grade;
};

/**
 * Checks if a grade is transferable (C- or better, or pass equivalent).
 * Returns false for failing grades, withdrawals, incomplete, audit, etc.
 */
export const isTransferableGrade = (grade: string | null | undefined): boolean => {
  if (grade == null) return true;
  const g = grade.trim();
  if (g.length === 0) return true;

  const upper = g.toUpperCase();

  const FAIL_MARKERS = new Set([
    'F',
    'FAIL',
    'NP',
    'N/P',
    'NOT PASSED',
    'W',
    'WD',
    'WITHDRAWN',
    'I',
    'INC',
    'INCOMPLETE',
    'AU',
    'AUDIT',
  ]);
  if (FAIL_MARKERS.has(upper)) return false;

  const PASS_MARKERS = new Set(['P', 'PASS', 'CR', 'CREDIT', 'TR', 'TRANSFER', 'S', 'SAT']);
  if (PASS_MARKERS.has(upper)) return true;

  const pct = upper.match(/^(\d{1,3})(\.\d+)?\s*%?$/);
  if (pct) return Number(pct[1]) >= 70;

  const letter = upper.match(/^([A-F])([+-])?$/);
  if (letter) {
    const base = letter[1];
    if (base === 'A' || base === 'B') return true;
    if (base === 'C') return true; // C-, C, C+ all pass (C- is the floor)
    return false; // D+, D, D-, F
  }

  return true;
};

export const sumCreditHours = (courses: ReviewCourse[]): number =>
  Math.round(courses.reduce((acc, c) => acc + (Number(c.creditHours) || 0), 0));

export const toCoursePayload = (courses: ReviewCourse[]): TranscriptCourseInput[] =>
  courses
    .filter((c) => c.rawName.trim().length > 0)
    .map(({ rawName, rawCode, creditHours, grade, categoryIds }) => ({
      rawName: rawName.trim(),
      rawCode: rawCode ?? null,
      creditHours:
        creditHours == null || `${creditHours}`.trim() === ''
          ? null
          : Math.round(Number(creditHours)),
      grade: normalizeGrade(grade ?? null),
      categoryIds: categoryIds ?? [],
    }))
    .filter((c) => isTransferableGrade(c.grade));

export const canSaveReview = (review: ReviewState): boolean =>
  review.institution.trim().length > 0 &&
  review.unofficialCredits !== null &&
  Number(review.unofficialCredits) >= 0;

export const useReviewEdits = (
  setReview: Dispatch<SetStateAction<ReviewState>>,
  nextKey: () => string
) => {
  const setInstitution = useCallback(
    (institution: string) => setReview((r) => ({ ...r, institution })),
    [setReview]
  );
  const setInstitutionUrl = useCallback(
    (institutionUrl: string) => setReview((r) => ({ ...r, institutionUrl })),
    [setReview]
  );
  const setAccreditingOrganizations = useCallback(
    (accreditingOrganizations: string[]) => setReview((r) => ({ ...r, accreditingOrganizations })),
    [setReview]
  );
  const setAwardedDegree = useCallback(
    (awardedDegree: string | null) => setReview((r) => ({ ...r, awardedDegree })),
    [setReview]
  );
  const setUnofficialCredits = useCallback(
    (unofficialCredits: number | null) => setReview((r) => ({ ...r, unofficialCredits })),
    [setReview]
  );
  const addCourse = useCallback(
    () =>
      setReview((r) => ({
        ...r,
        courses: [
          ...r.courses,
          {
            key: nextKey(),
            rawName: '',
            rawCode: null,
            creditHours: null,
            grade: null,
            categoryIds: [],
          },
        ],
      })),
    [setReview, nextKey]
  );
  const updateCourse = useCallback(
    (key: string, patch: Partial<TranscriptCourseInput>) =>
      setReview((r) => ({
        ...r,
        courses: r.courses.map((c) => (c.key === key ? { ...c, ...patch } : c)),
      })),
    [setReview]
  );
  const removeCourse = useCallback(
    (key: string) => setReview((r) => ({ ...r, courses: r.courses.filter((c) => c.key !== key) })),
    [setReview]
  );

  return {
    setInstitution,
    setInstitutionUrl,
    setAccreditingOrganizations,
    setAwardedDegree,
    setUnofficialCredits,
    addCourse,
    updateCourse,
    removeCourse,
  };
};
